import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  User,
  Sector,
  Subject,
  QuestionSet,
  Question,
  Attempt,
  UserRole,
} from '../types';
import {
  INITIAL_USERS,
  INITIAL_SECTORS,
  INITIAL_SUBJECTS,
  INITIAL_QUESTION_SETS,
  INITIAL_QUESTIONS,
  INITIAL_ATTEMPTS,
} from '../data/seedData';

export type AppView =
  | 'student-dashboard'
  | 'student-sets'
  | 'quiz-revision'
  | 'quiz-test'
  | 'quiz-scorecard'
  | 'student-analytics'
  | 'admin-dashboard'
  | 'admin-sectors'
  | 'admin-subjects'
  | 'admin-sets'
  | 'admin-questions'
  | 'admin-attempts'
  | 'admin-users';

interface AppContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  isProfileCompleted: boolean;
  isLoading: boolean;
  users: User[];
  sectors: Sector[];
  subjects: Subject[];
  questionSets: QuestionSet[];
  questions: Question[];
  attempts: Attempt[];
  currentView: AppView;
  selectedSectorId: number | null;
  selectedSubjectId: number | null;
  activeQuestionSet: QuestionSet | null;
  latestAttempt: Attempt | null;
  authModalOpen: boolean;
  questionLanguage: 'en' | 'hi';

  // Navigation
  setCurrentView: (view: AppView) => void;
  setSelectedSectorId: (id: number | null) => void;
  setSelectedSubjectId: (id: number | null) => void;
  setActiveQuestionSet: (set: QuestionSet | null) => void;
  setLatestAttempt: (attempt: Attempt | null) => void;
  setAuthModalOpen: (open: boolean) => void;
  setQuestionLanguage: (lang: 'en' | 'hi') => void;

  // Auth & Roles
  setCurrentUser: (user: User | null) => void;
  sendOtp: (mobile: string) => Promise<{ success: boolean; message?: string; dev_otp?: string | null; error?: string }>;
  verifyOtp: (mobile: string, otp: string) => Promise<{ success: boolean; is_profile_completed?: number; error?: string }>;
  completeProfile: (name: string, email?: string, city?: string, profilePicture?: string) => Promise<{ success: boolean; error?: string }>;
  logoutUser: () => Promise<void>;
  checkAuth: () => Promise<void>;

  // User Management & Impersonation
  fetchUsers: () => Promise<void>;
  createUser: (userData: { name: string; mobile: string; email?: string; city?: string; role: 'student' | 'admin'; status: 'active' | 'inactive' }) => Promise<{ success: boolean; error?: string }>;
  updateUser: (id: number, userData: { name: string; mobile: string; email?: string; city?: string; role: 'student' | 'admin'; status: 'active' | 'inactive' }) => Promise<{ success: boolean; error?: string }>;
  deleteUser: (id: number) => Promise<{ success: boolean; action?: string; message?: string; error?: string }>;
  impersonateUser: (id: number) => Promise<{ success: boolean; error?: string }>;
  returnToAdmin: () => Promise<{ success: boolean; error?: string }>;

  // Sectors
  addSector: (sector: Omit<Sector, 'id'>) => void;
  updateSector: (id: number, updates: Partial<Sector>) => void;
  deleteSector: (id: number) => void;

  // Subjects
  addSubject: (subject: Omit<Subject, 'id'>) => void;
  updateSubject: (id: number, updates: Partial<Subject>) => void;
  deleteSubject: (id: number) => void;

  // Question Sets
  addQuestionSet: (qSet: Omit<QuestionSet, 'id'>) => void;
  updateQuestionSet: (id: number, updates: Partial<QuestionSet>) => void;
  deleteQuestionSet: (id: number) => void;

  // Questions
  addQuestion: (question: Omit<Question, 'id'>) => void;
  updateQuestion: (id: number, updates: Partial<Question>) => void;
  deleteQuestion: (id: number) => void;
  clearQuestionsInSet: (questionSetId: number) => Promise<boolean>;
  importQuestions: (
    newQuestions: Omit<Question, 'id'>[],
    options?: { replaceExisting?: boolean; targetSetId?: number }
  ) => Promise<number>;

  // Attempts
  fetchAttempts: () => Promise<void>;
  deleteAttempt: (id: number) => Promise<{ success: boolean; message?: string; error?: string }>;
  deleteAllAttempts: () => Promise<{ success: boolean; message?: string; error?: string }>;
  saveAttempt: (attempt: Omit<Attempt, 'id'>) => Attempt;

  // Reset
  resetToDefaultData: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const STORAGE_KEYS = {
  USERS: 'mcq_users_v2',
  SECTORS: 'mcq_sectors_v2',
  SUBJECTS: 'mcq_subjects_v2',
  QUESTION_SETS: 'mcq_question_sets_v2',
  QUESTIONS: 'mcq_questions_v2',
  ATTEMPTS: 'mcq_attempts_v2',
};

function loadStorage<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (e) {
    console.error(`Error loading ${key} from storage:`, e);
    return defaultValue;
  }
}

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isProfileCompleted, setIsProfileCompleted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [questionSets, setQuestionSets] = useState<QuestionSet[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [attempts, setAttempts] = useState<Attempt[]>([]);

  const [currentView, setCurrentView] = useState<AppView>('student-dashboard');
  const [selectedSectorId, setSelectedSectorId] = useState<number | null>(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState<number | null>(null);
  const [activeQuestionSet, setActiveQuestionSet] = useState<QuestionSet | null>(null);
  const [latestAttempt, setLatestAttempt] = useState<Attempt | null>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [questionLanguage, setQuestionLanguage] = useState<'en' | 'hi'>('en');

  // Fetch functions for MySQL API data
  const fetchSectors = async () => {
    try {
      const res = await fetch('/api/sectors.php?action=list', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.sectors)) {
          setSectors(data.sectors);
        }
      }
    } catch (e) {
      console.error('Fetch sectors error:', e);
    }
  };

  const fetchSubjects = async () => {
    try {
      const res = await fetch('/api/subjects.php?action=list', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.subjects)) {
          setSubjects(data.subjects);
        }
      }
    } catch (e) {
      console.error('Fetch subjects error:', e);
    }
  };

  const fetchQuestionSets = async () => {
    try {
      const res = await fetch('/api/question_sets.php?action=list', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.question_sets)) {
          setQuestionSets(data.question_sets);
        }
      }
    } catch (e) {
      console.error('Fetch question sets error:', e);
    }
  };

  const fetchQuestions = async () => {
    try {
      const res = await fetch('/api/questions.php?action=list', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.questions)) {
          setQuestions(data.questions);
        }
      }
    } catch (e) {
      console.error('Fetch questions error:', e);
    }
  };

  const fetchAttempts = async () => {
    try {
      const res = await fetch('/api/attempts.php?action=list', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.attempts)) {
          setAttempts(data.attempts);
          if (data.attempts.length > 0) {
            setLatestAttempt(data.attempts[0]);
          }
        }
      }
    } catch (e) {
      console.error('Fetch attempts error:', e);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch('/api/users.php?action=list', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.users)) {
          setUsers(data.users);
        }
      }
    } catch (e) {
      console.error('Fetch users error:', e);
    }
  };

  const refreshAllData = async () => {
    await Promise.all([
      fetchSectors(),
      fetchSubjects(),
      fetchQuestionSets(),
      fetchQuestions(),
      fetchAttempts(),
    ]);
  };

  const checkAuth = async () => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/auth.php?action=check', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.authenticated && data.user) {
          setCurrentUser(data.user);
          setIsAuthenticated(true);
          const completed = !!data.user.is_profile_completed;
          setIsProfileCompleted(completed);

          if (!completed) {
            setAuthModalOpen(true);
          } else {
            setAuthModalOpen(false);
          }

          if (data.user.role === 'admin') {
            fetchUsers();
          }
          refreshAllData();
          setIsLoading(false);
          return;
        }
      }
    } catch (e) {
      console.error('Check auth error:', e);
    }
    setCurrentUser(null);
    setIsAuthenticated(false);
    setIsProfileCompleted(false);
    setAuthModalOpen(true);
    refreshAllData();
    setIsLoading(false);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // User Management & Impersonation
  const createUser = async (userData: { name: string; email: string; mobile?: string; password: string; role: 'student' | 'admin'; status: 'active' | 'inactive' }): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch('/api/users.php?action=create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(userData),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        await fetchUsers();
        return { success: true };
      }
      return { success: false, error: data?.error || 'Failed to create user.' };
    } catch (e: any) {
      return { success: false, error: e.message || 'Network error' };
    }
  };

  const updateUser = async (id: number, userData: { name: string; email: string; mobile?: string; password?: string; role: 'student' | 'admin'; status: 'active' | 'inactive' }): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch('/api/users.php?action=update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id, ...userData }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        await fetchUsers();
        return { success: true };
      }
      return { success: false, error: data?.error || 'Failed to update user.' };
    } catch (e: any) {
      return { success: false, error: e.message || 'Network error' };
    }
  };

  const deleteUser = async (id: number): Promise<{ success: boolean; action?: string; message?: string; error?: string }> => {
    try {
      const res = await fetch('/api/users.php?action=delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        await fetchUsers();
        await fetchAttempts();
        return { success: true, action: data.action, message: data.message };
      }
      return { success: false, error: data?.error || 'Failed to delete user.' };
    } catch (e: any) {
      return { success: false, error: e.message || 'Network error' };
    }
  };

  const deleteAttempt = async (id: number): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      const res = await fetch('/api/attempts.php?action=delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        await fetchAttempts();
        return { success: true, message: data.message };
      }
      return { success: false, error: data?.error || 'Failed to delete submission.' };
    } catch (e: any) {
      return { success: false, error: e.message || 'Network error' };
    }
  };

  const deleteAllAttempts = async (): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      const res = await fetch('/api/attempts.php?action=delete_all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        await fetchAttempts();
        return { success: true, message: data.message };
      }
      return { success: false, error: data?.error || 'Failed to delete all submissions.' };
    } catch (e: any) {
      return { success: false, error: e.message || 'Network error' };
    }
  };

  const impersonateUser = async (id: number): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch('/api/users.php?action=impersonate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success && data?.user) {
        setCurrentUser(data.user);
        if (data.user.role === 'admin') {
          setCurrentView('admin-dashboard');
        } else {
          setCurrentView('student-dashboard');
        }
        await refreshAllData();
        return { success: true };
      }
      return { success: false, error: data?.error || 'Failed to start impersonation session.' };
    } catch (e: any) {
      return { success: false, error: e.message || 'Network error' };
    }
  };

  const returnToAdmin = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch('/api/users.php?action=return_to_admin', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success && data?.user) {
        setCurrentUser(data.user);
        setCurrentView('admin-dashboard');
        await fetchUsers();
        await refreshAllData();
        return { success: true };
      }
      return { success: false, error: data?.error || 'Failed to return to admin session.' };
    } catch (e: any) {
      return { success: false, error: e.message || 'Network error' };
    }
  };

  const sendOtp = async (mobile: string): Promise<{ success: boolean; message?: string; dev_otp?: string | null; error?: string }> => {
    try {
      const res = await fetch('/api/auth.php?action=send_otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mobile }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        return { success: true, message: data.message, dev_otp: data.dev_otp };
      }
      return { success: false, error: data?.error || 'Failed to send OTP. Please check mobile number.' };
    } catch (e: any) {
      return { success: false, error: e.message || 'Network error' };
    }
  };

  const verifyOtp = async (mobile: string, otp: string): Promise<{ success: boolean; is_profile_completed?: number; error?: string }> => {
    try {
      const res = await fetch('/api/auth.php?action=verify_otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mobile, otp }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success && data?.user) {
        setCurrentUser(data.user);
        setIsAuthenticated(true);
        const completed = !!data.user.is_profile_completed;
        setIsProfileCompleted(completed);

        if (data.user.role === 'admin') {
          setCurrentView('admin-dashboard');
          fetchUsers();
        } else {
          setCurrentView('student-dashboard');
        }

        if (!completed) {
          setAuthModalOpen(true);
        } else {
          setAuthModalOpen(false);
        }

        await refreshAllData();
        return { success: true, is_profile_completed: data.is_profile_completed };
      }
      return { success: false, error: data?.error || 'Invalid OTP code.' };
    } catch (e: any) {
      return { success: false, error: e.message || 'Network error' };
    }
  };

  const completeProfile = async (
    name: string,
    email?: string,
    city?: string,
    profilePicture?: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch('/api/auth.php?action=complete_profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name,
          email: email || '',
          city: city || '',
          profile_picture: profilePicture || '',
        }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success && data?.user) {
        setCurrentUser(data.user);
        setIsAuthenticated(true);
        setIsProfileCompleted(true);
        setAuthModalOpen(false);
        await refreshAllData();
        return { success: true };
      }
      return { success: false, error: data?.error || 'Failed to complete profile.' };
    } catch (e: any) {
      return { success: false, error: e.message || 'Network error' };
    }
  };

  const logoutUser = async () => {
    try {
      await fetch('/api/auth.php?action=logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (e) {
      console.error('Logout error:', e);
    }
    setCurrentUser(null);
    setIsAuthenticated(false);
    setIsProfileCompleted(false);
    setAuthModalOpen(true);
    setCurrentView('student-dashboard');
    await refreshAllData();
  };

  // Sectors API CRUD
  const addSector = async (data: Omit<Sector, 'id'>) => {
    try {
      const res = await fetch('/api/sectors.php?action=create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (res.ok) {
        await fetchSectors();
      }
    } catch (e) {
      console.error('Add sector error:', e);
    }
  };

  const updateSector = async (id: number, updates: Partial<Sector>) => {
    try {
      const current = sectors.find(s => s.id === id);
      if (!current) return;
      const res = await fetch('/api/sectors.php?action=update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id, ...current, ...updates }),
      });
      if (res.ok) {
        await fetchSectors();
      }
    } catch (e) {
      console.error('Update sector error:', e);
    }
  };

  const deleteSector = async (id: number) => {
    try {
      const res = await fetch('/api/sectors.php?action=delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      if (res.ok) {
        await fetchSectors();
        await fetchSubjects();
        await fetchQuestionSets();
      }
    } catch (e) {
      console.error('Delete sector error:', e);
    }
  };

  // Subjects API CRUD
  const addSubject = async (data: Omit<Subject, 'id'>) => {
    try {
      const res = await fetch('/api/subjects.php?action=create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (res.ok) {
        await fetchSubjects();
      }
    } catch (e) {
      console.error('Add subject error:', e);
    }
  };

  const updateSubject = async (id: number, updates: Partial<Subject>) => {
    try {
      const current = subjects.find(s => s.id === id);
      if (!current) return;
      const res = await fetch('/api/subjects.php?action=update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id, ...current, ...updates }),
      });
      if (res.ok) {
        await fetchSubjects();
      }
    } catch (e) {
      console.error('Update subject error:', e);
    }
  };

  const deleteSubject = async (id: number) => {
    try {
      const res = await fetch('/api/subjects.php?action=delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      if (res.ok) {
        await fetchSubjects();
        await fetchQuestionSets();
      }
    } catch (e) {
      console.error('Delete subject error:', e);
    }
  };

  // Question Sets API CRUD
  const addQuestionSet = async (data: Omit<QuestionSet, 'id'>) => {
    try {
      const res = await fetch('/api/question_sets.php?action=create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (res.ok) {
        await fetchQuestionSets();
      }
    } catch (e) {
      console.error('Add question set error:', e);
    }
  };

  const updateQuestionSet = async (id: number, updates: Partial<QuestionSet>) => {
    try {
      const current = questionSets.find(s => s.id === id);
      if (!current) return;
      const res = await fetch('/api/question_sets.php?action=update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id, ...current, ...updates }),
      });
      if (res.ok) {
        await fetchQuestionSets();
      }
    } catch (e) {
      console.error('Update question set error:', e);
    }
  };

  const deleteQuestionSet = async (id: number) => {
    try {
      const res = await fetch('/api/question_sets.php?action=delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      if (res.ok) {
        await fetchQuestionSets();
        await fetchQuestions();
      }
    } catch (e) {
      console.error('Delete question set error:', e);
    }
  };

  // Questions API CRUD
  const addQuestion = async (data: Omit<Question, 'id'>) => {
    try {
      const res = await fetch('/api/questions.php?action=create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (res.ok) {
        await fetchQuestions();
      }
    } catch (e) {
      console.error('Add question error:', e);
    }
  };

  const updateQuestion = async (id: number, updates: Partial<Question>) => {
    try {
      const current = questions.find(q => q.id === id);
      if (!current) return;
      const res = await fetch('/api/questions.php?action=update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id, ...current, ...updates }),
      });
      if (res.ok) {
        await fetchQuestions();
      }
    } catch (e) {
      console.error('Update question error:', e);
    }
  };

  const deleteQuestion = async (id: number) => {
    try {
      const res = await fetch('/api/questions.php?action=delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      if (res.ok) {
        await fetchQuestions();
      }
    } catch (e) {
      console.error('Delete question error:', e);
    }
  };

  const clearQuestionsInSet = async (questionSetId: number): Promise<boolean> => {
    try {
      const res = await fetch('/api/questions.php?action=clear_set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ question_set_id: questionSetId }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        await fetchQuestions();
        return true;
      }
      return false;
    } catch (e) {
      console.error('Clear questions in set error:', e);
      return false;
    }
  };

  const importQuestions = async (
    newQuestions: Omit<Question, 'id'>[],
    options?: { replaceExisting?: boolean; targetSetId?: number }
  ): Promise<number> => {
    try {
      const res = await fetch('/api/questions.php?action=import_csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          questions: newQuestions,
          replace_existing: options?.replaceExisting || false,
          target_set_id: options?.targetSetId || 0,
        }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        await fetchQuestions();
        return data.count || newQuestions.length;
      }
      return 0;
    } catch (e) {
      console.error('Import questions API error:', e);
      return 0;
    }
  };

  // Attempts API
  const saveAttempt = (data: Omit<Attempt, 'id'>): Attempt => {
    fetchAttempts();
    return {
      ...data,
      id: Date.now(),
      user_name: currentUser ? currentUser.name : 'Student',
    };
  };

  const resetToDefaultData = () => {
    refreshAllData();
  };

  return (
    <AppContext.Provider
      value={{
        currentUser,
        isAuthenticated,
        isProfileCompleted,
        isLoading,
        users,
        sectors,
        subjects,
        questionSets,
        questions,
        attempts,
        currentView,
        selectedSectorId,
        selectedSubjectId,
        activeQuestionSet,
        latestAttempt,
        authModalOpen,
        questionLanguage,
        setQuestionLanguage,

        setCurrentView,
        setSelectedSectorId,
        setSelectedSubjectId,
        setActiveQuestionSet,
        setLatestAttempt,
        setAuthModalOpen,

        setCurrentUser,
        sendOtp,
        verifyOtp,
        completeProfile,
        logoutUser,
        checkAuth,

        fetchUsers,
        createUser,
        updateUser,
        deleteUser,
        impersonateUser,
        returnToAdmin,

        addSector,
        updateSector,
        deleteSector,

        addSubject,
        updateSubject,
        deleteSubject,

        addQuestionSet,
        updateQuestionSet,
        deleteQuestionSet,

        addQuestion,
        updateQuestion,
        deleteQuestion,
        clearQuestionsInSet,
        importQuestions,

        saveAttempt,
        fetchAttempts,
        deleteAttempt,
        deleteAllAttempts,
        resetToDefaultData,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
